import React, { useState, useEffect, useCallback } from 'react';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { tournamentsService, matchesService } from '../../../services/supabaseService';
import Toast from '../../../components/ui/Toast';

// Move PredictionCard outside to prevent recreation on every render
const PredictionCard = React.memo(({ title, description, enabled, points, onToggle, onPointsChange, additionalFields = null }) => (
  <div className="bg-card border border-border rounded-lg p-4 md:p-6">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex-1 min-w-0">
        <h4 className="text-base font-semibold text-foreground mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Checkbox
        checked={enabled}
        onChange={onToggle}
        className="flex-shrink-0"
      />
    </div>

    {enabled && (
      <div className="space-y-4 pt-4 border-t border-border">
        <Input
          label="Points"
          type="number"
          value={points}
          onChange={(e) => onPointsChange(e?.target?.value)}
          placeholder="0"
        />
        {additionalFields}
      </div>
    )}
  </div>
));

PredictionCard.displayName = 'PredictionCard';

const PredictionSettingsTab = ({ tournamentId }) => {
  const [settings, setSettings] = useState({
    tournamentLevel: {
      championPrediction: { enabled: true, points: 50 },
      playerOfTournament: { enabled: true, points: 30 },
      semifinalistTeams: { enabled: true, points: 20, tieupPoints: 10 },
      finalistTeams: { enabled: true, points: 25 },
      top4Teams: { enabled: false, points: 10, orderPoints: 5 },
      superOverTeam: { enabled: false, points: 15 },
      autoEnableDays: 7,
      autoLockBeforeFirstMatch: true,
      extendedLockHours: 0
    },
    matchLevel: {
      matchWinner: { enabled: true, points: 10 },
      manOfMatch: { enabled: true, points: 15 },
      tossWinner: { enabled: false, points: 5 },
      battingFirst: { enabled: false, points: 5 },
      highestRunScorer: { enabled: true, points: 20 },
      highestWicketTaker: { enabled: true, points: 20 },
      highestRunConcedingBowler: { enabled: false, points: 15 },
      autoLockHours: 1,
      autoEnableDays: 1
    },
    bonusPoints: {
      earlyPrediction: { enabled: true, days: 3, points: 5 },
      confidenceBonus: { enabled: true, highConfidence: 10, mediumConfidence: 5 }
    }
  });

  const [activeSection, setActiveSection] = useState('tournament');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Load existing settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      if (!tournamentId || tournamentId === 'new') return;
      
      setLoading(true);
      try {
        const existingSettings = await tournamentsService?.getPredictionSettings(tournamentId);
        if (existingSettings) {
          setSettings(existingSettings);
        }
      } catch (error) {
        console.error('Error loading prediction settings:', error);
        setToast({ message: 'Failed to load prediction settings', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [tournamentId]);

  const handleTogglePrediction = useCallback((level, predictionType) => {
    setSettings(prev => ({
      ...prev,
      [level]: {
        ...prev?.[level],
        [predictionType]: {
          ...prev?.[level]?.[predictionType],
          enabled: !prev?.[level]?.[predictionType]?.enabled
        }
      }
    }));
  }, []);

  const handlePointsChange = useCallback((level, predictionType, value) => {
    setSettings(prev => ({
      ...prev,
      [level]: {
        ...prev?.[level],
        [predictionType]: {
          ...prev?.[level]?.[predictionType],
          points: parseInt(value) || 0
        }
      }
    }));
  }, []);

  // Stable handlers for nested settings
  const handleTieupPointsChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      tournamentLevel: {
        ...prev?.tournamentLevel,
        semifinalistTeams: {
          ...prev?.tournamentLevel?.semifinalistTeams,
          tieupPoints: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleAutoEnableDaysChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      tournamentLevel: {
        ...prev?.tournamentLevel,
        autoEnableDays: parseInt(value) || 0
      }
    }));
  }, []);

  const handleAutoLockBeforeFirstMatchChange = useCallback((checked) => {
    setSettings(prev => ({
      ...prev,
      tournamentLevel: {
        ...prev?.tournamentLevel,
        autoLockBeforeFirstMatch: checked
      }
    }));
  }, []);

  const handleExtendedLockHoursChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      tournamentLevel: {
        ...prev?.tournamentLevel,
        extendedLockHours: parseInt(value) || 0
      }
    }));
  }, []);

  const handleAutoLockHoursChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      matchLevel: {
        ...prev?.matchLevel,
        autoLockHours: parseInt(value) || 0
      }
    }));
  }, []);

  const handleMatchAutoEnableDaysChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      matchLevel: {
        ...prev?.matchLevel,
        autoEnableDays: parseInt(value) || 0
      }
    }));
  }, []);

  const handleEarlyPredictionToggle = useCallback((checked) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        earlyPrediction: {
          ...prev?.bonusPoints?.earlyPrediction,
          enabled: checked
        }
      }
    }));
  }, []);

  const handleEarlyPredictionDaysChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        earlyPrediction: {
          ...prev?.bonusPoints?.earlyPrediction,
          days: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleEarlyPredictionPointsChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        earlyPrediction: {
          ...prev?.bonusPoints?.earlyPrediction,
          points: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleConfidenceBonusToggle = useCallback((checked) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        confidenceBonus: {
          ...prev?.bonusPoints?.confidenceBonus,
          enabled: checked
        }
      }
    }));
  }, []);

  const handleHighConfidenceChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        confidenceBonus: {
          ...prev?.bonusPoints?.confidenceBonus,
          highConfidence: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleMediumConfidenceChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      bonusPoints: {
        ...prev?.bonusPoints,
        confidenceBonus: {
          ...prev?.bonusPoints?.confidenceBonus,
          mediumConfidence: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleTop4OrderPointsChange = useCallback((value) => {
    setSettings(prev => ({
      ...prev,
      tournamentLevel: {
        ...prev?.tournamentLevel,
        top4Teams: {
          ...prev?.tournamentLevel?.top4Teams,
          orderPoints: parseInt(value) || 0
        }
      }
    }));
  }, []);

  const handleSaveSettings = async () => {
    if (!tournamentId) {
      setToast({ message: 'Tournament ID is missing', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      await tournamentsService?.savePredictionSettings(tournamentId, settings);

      // Apply auto-enable to existing and future scheduled matches
      const autoEnableDays = settings?.matchLevel?.autoEnableDays ?? 1;
      try {
        const result = await matchesService?.enableMatchPredictionsBySettings(tournamentId, autoEnableDays);
        const enabledMsg = result?.enabled > 0 ? ` Enabled predictions for ${result?.enabled} match(es).` : '';
        setToast({ message: `Prediction settings saved successfully.${enabledMsg}`, type: 'success' });
      } catch (enableError) {
        console.error('Error enabling match predictions:', enableError);
        setToast({ message: 'Prediction settings saved. Could not auto-enable match predictions.', type: 'warning' });
      }
    } catch (error) {
      console.error('Error saving prediction settings:', error);
      setToast({ message: 'Failed to save prediction settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Prediction Settings</h3>
          <p className="text-sm text-muted-foreground mt-1">Configure point systems and prediction rules</p>
        </div>
        <Button
          variant="default"
          onClick={handleSaveSettings}
          iconName="Save"
          iconPosition="left"
          className="w-full sm:w-auto"
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={activeSection === 'tournament' ? 'default' : 'outline'}
          onClick={() => setActiveSection('tournament')}
          size="sm"
        >
          Tournament Level
        </Button>
        <Button
          variant={activeSection === 'match' ? 'default' : 'outline'}
          onClick={() => setActiveSection('match')}
          size="sm"
        >
          Match Level
        </Button>
        <Button
          variant={activeSection === 'bonus' ? 'default' : 'outline'}
          onClick={() => setActiveSection('bonus')}
          size="sm"
        >
          Bonus Points
        </Button>
      </div>
      {activeSection === 'tournament' && (
        <div className="space-y-4">
          <PredictionCard
            title="Champion Prediction"
            description="Predict the tournament winner"
            enabled={settings?.tournamentLevel?.championPrediction?.enabled}
            points={settings?.tournamentLevel?.championPrediction?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'championPrediction')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'championPrediction', value)}
          />

          <PredictionCard
            title="Player of Tournament"
            description="Predict the best player of the tournament"
            enabled={settings?.tournamentLevel?.playerOfTournament?.enabled}
            points={settings?.tournamentLevel?.playerOfTournament?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'playerOfTournament')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'playerOfTournament', value)}
          />

          <PredictionCard
            title="Semifinalist Teams"
            description="Predict teams reaching semifinals"
            enabled={settings?.tournamentLevel?.semifinalistTeams?.enabled}
            points={settings?.tournamentLevel?.semifinalistTeams?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'semifinalistTeams')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'semifinalistTeams', value)}
            additionalFields={
              <Input
                label="Tieup Points (for correct teams)"
                type="number"
                value={settings?.tournamentLevel?.semifinalistTeams?.tieupPoints}
                onChange={(e) => handleTieupPointsChange(e?.target?.value)}
                placeholder="0"
              />
            }
          />

          <PredictionCard
            title="Finalist Teams"
            description="Predict teams reaching the final"
            enabled={settings?.tournamentLevel?.finalistTeams?.enabled}
            points={settings?.tournamentLevel?.finalistTeams?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'finalistTeams')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'finalistTeams', value)}
          />

          <PredictionCard
            title="Select Top 4 Teams"
            description="Predict the top 4 teams of the tournament. Earn points for each correct team and bonus points for correct order."
            enabled={settings?.tournamentLevel?.top4Teams?.enabled}
            points={settings?.tournamentLevel?.top4Teams?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'top4Teams')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'top4Teams', value)}
            additionalFields={
              <Input
                label="Points per correct order position"
                type="number"
                value={settings?.tournamentLevel?.top4Teams?.orderPoints}
                onChange={(e) => handleTop4OrderPointsChange(e?.target?.value)}
                placeholder="0"
                description="Extra points awarded when the team is predicted in the correct position"
              />
            }
          />

          <PredictionCard
            title="Select Team That Goes Into Super Over"
            description="Predict which team will be involved in a super over. Points are earned when a match goes to super over and the user predicted correctly."
            enabled={settings?.tournamentLevel?.superOverTeam?.enabled}
            points={settings?.tournamentLevel?.superOverTeam?.points}
            onToggle={() => handleTogglePrediction('tournamentLevel', 'superOverTeam')}
            onPointsChange={(value) => handlePointsChange('tournamentLevel', 'superOverTeam', value)}
          />

          <div className="bg-muted rounded-lg p-4 md:p-6 space-y-4">
            <h4 className="text-base font-semibold text-foreground">Auto-Enable Settings</h4>
            <Input
              label="Enable predictions (days before tournament)"
              type="number"
              value={settings?.tournamentLevel?.autoEnableDays}
              onChange={(e) => handleAutoEnableDaysChange(e?.target?.value)}
              description="Predictions will automatically open this many days before tournament start"
            />
            <Checkbox
              label="Auto-lock before first match"
              checked={settings?.tournamentLevel?.autoLockBeforeFirstMatch}
              onChange={(e) => handleAutoLockBeforeFirstMatchChange(e?.target?.checked)}
            />
            <Input
              label="Extended lock time (hours after auto-lock)"
              type="number"
              value={settings?.tournamentLevel?.extendedLockHours}
              onChange={(e) => handleExtendedLockHoursChange(e?.target?.value)}
              description="Allow predictions for this many hours after auto-lock time. Set to 0 to disable."
            />
          </div>
        </div>
      )}
      {activeSection === 'match' && (
        <div className="space-y-4">
          <PredictionCard
            title="Match Winner"
            description="Predict the winning team"
            enabled={settings?.matchLevel?.matchWinner?.enabled}
            points={settings?.matchLevel?.matchWinner?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'matchWinner')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'matchWinner', value)}
          />

          <PredictionCard
            title="Man of the Match"
            description="Predict the best player of the match"
            enabled={settings?.matchLevel?.manOfMatch?.enabled}
            points={settings?.matchLevel?.manOfMatch?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'manOfMatch')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'manOfMatch', value)}
          />

          <PredictionCard
            title="Toss Winner (Knockout Only)"
            description="Predict which team wins the toss"
            enabled={settings?.matchLevel?.tossWinner?.enabled}
            points={settings?.matchLevel?.tossWinner?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'tossWinner')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'tossWinner', value)}
          />

          <PredictionCard
            title="Batting First (Knockout Only)"
            description="Predict which team bats first"
            enabled={settings?.matchLevel?.battingFirst?.enabled}
            points={settings?.matchLevel?.battingFirst?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'battingFirst')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'battingFirst', value)}
          />

          <PredictionCard
            title="Highest Run Scorer"
            description="Predict the player with most runs"
            enabled={settings?.matchLevel?.highestRunScorer?.enabled}
            points={settings?.matchLevel?.highestRunScorer?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'highestRunScorer')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'highestRunScorer', value)}
          />

          <PredictionCard
            title="Highest Wicket Taker"
            description="Predict the player with most wickets"
            enabled={settings?.matchLevel?.highestWicketTaker?.enabled}
            points={settings?.matchLevel?.highestWicketTaker?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'highestWicketTaker')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'highestWicketTaker', value)}
          />

          <PredictionCard
            title="Highest Run Conceding Bowler (Knockout Only)"
            description="Predict the bowler who concedes most runs"
            enabled={settings?.matchLevel?.highestRunConcedingBowler?.enabled}
            points={settings?.matchLevel?.highestRunConcedingBowler?.points}
            onToggle={() => handleTogglePrediction('matchLevel', 'highestRunConcedingBowler')}
            onPointsChange={(value) => handlePointsChange('matchLevel', 'highestRunConcedingBowler', value)}
          />

          <div className="bg-muted rounded-lg p-4 md:p-6 space-y-4">
            <h4 className="text-base font-semibold text-foreground">Match Prediction Timing</h4>
            <Input
              label="Enable predictions (days before match)"
              type="number"
              value={settings?.matchLevel?.autoEnableDays}
              onChange={(e) => handleMatchAutoEnableDaysChange(e?.target?.value)}
              description="Match predictions will open this many days before the scheduled match date and time. Set to 0 to enable immediately."
            />
            <Input
              label="Auto-lock time (hours before match)"
              type="number"
              value={settings?.matchLevel?.autoLockHours}
              onChange={(e) => handleAutoLockHoursChange(e?.target?.value)}
              description="Match predictions will lock this many hours before match start"
            />
          </div>
        </div>
      )}
      {activeSection === 'bonus' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-foreground mb-1">Early Prediction Bonus</h4>
                <p className="text-sm text-muted-foreground">Reward users for making predictions early</p>
              </div>
              <Checkbox
                checked={settings?.bonusPoints?.earlyPrediction?.enabled}
                onChange={(e) => handleEarlyPredictionToggle(e?.target?.checked)}
                className="flex-shrink-0"
              />
            </div>

            {settings?.bonusPoints?.earlyPrediction?.enabled && (
              <div className="space-y-4 pt-4 border-t border-border">
                <Input
                  label="Days before deadline"
                  type="number"
                  value={settings?.bonusPoints?.earlyPrediction?.days}
                  onChange={(e) => handleEarlyPredictionDaysChange(e?.target?.value)}
                  description="Bonus applies if prediction made this many days before deadline"
                />
                <Input
                  label="Bonus Points"
                  type="number"
                  value={settings?.bonusPoints?.earlyPrediction?.points}
                  onChange={(e) => handleEarlyPredictionPointsChange(e?.target?.value)}
                />
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-foreground mb-1">Confidence Level Bonus</h4>
                <p className="text-sm text-muted-foreground">Reward users based on prediction confidence</p>
              </div>
              <Checkbox
                checked={settings?.bonusPoints?.confidenceBonus?.enabled}
                onChange={(e) => handleConfidenceBonusToggle(e?.target?.checked)}
                className="flex-shrink-0"
              />
            </div>

            {settings?.bonusPoints?.confidenceBonus?.enabled && (
              <div className="space-y-4 pt-4 border-t border-border">
                <Input
                  label="High Confidence Bonus"
                  type="number"
                  value={settings?.bonusPoints?.confidenceBonus?.highConfidence}
                  onChange={(e) => handleHighConfidenceChange(e?.target?.value)}
                />
                <Input
                  label="Medium Confidence Bonus"
                  type="number"
                  value={settings?.bonusPoints?.confidenceBonus?.mediumConfidence}
                  onChange={(e) => handleMediumConfidenceChange(e?.target?.value)}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PredictionSettingsTab;